import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { langToSlug } from '../hooks/useLocalePath';
import { renderT } from '../i18n/renderT';
import IndustryTabs from '../components/features/IndustryTabs';

const Industries: React.FC = () => {
    useScrollReveal();
    const { language, t } = useLanguage();
    const lp = (path: string) => `/${langToSlug(language)}${path}`;

    return (
        <main>
            <SEOHead pageId="industries" />
            {/* ═══════════════════════════ HERO ═══════════════════════════ */}
            <section className="ind-hero section" id="industries-hero">
                <div className="container">
                    <div className="ind-hero-grid">
                        <div className="ind-hero-text">
                            <div className="section-label reveal">{t('ind.heroLabel')}</div>
                            <h1 className="ind-hero-headline reveal">
                                {renderT(t('ind.heroHeadline'))}
                            </h1>
                            <p className="ind-hero-sub reveal reveal-d1">
                                {t('ind.heroSub')}
                            </p>
                        </div>

                        {/* Framework diagram */}
                        <div className="fw-wrap reveal reveal-d2">
                            <div className="fw-bar">
                                <div className="dot dot-r" />
                                <div className="dot dot-y" />
                                <div className="dot dot-g" />
                                <span>industry-framework.map</span>
                            </div>
                            <div className="fw-body">
                                {/* Vertical industry columns */}
                                <div className="fw-verticals">
                                    <div className="fw-vert v1">
                                        <div className="fw-vert-icon">
                                            <svg viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                                        </div>
                                        <div className="fw-vert-name">Retail &amp;<br />E-commerce</div>
                                    </div>
                                    <div className="fw-vert v2">
                                        <div className="fw-vert-icon">
                                            <svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                                        </div>
                                        <div className="fw-vert-name">Financial<br />Services</div>
                                    </div>
                                    <div className="fw-vert v3">
                                        <div className="fw-vert-icon">
                                            <svg viewBox="0 0 24 24"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                                        </div>
                                        <div className="fw-vert-name">Logistics &amp;<br />Supply Chain</div>
                                    </div>
                                    <div className="fw-vert v4">
                                        <div className="fw-vert-icon">
                                            <svg viewBox="0 0 24 24"><path d="M2 20h20"/><path d="M5 20V8l5 4V8l5 4V4l5 4v12"/></svg>
                                        </div>
                                        <div className="fw-vert-name">Manufac-<br />turing</div>
                                    </div>
                                    <div className="fw-vert v5">
                                        <div className="fw-vert-icon">
                                            <svg viewBox="0 0 24 24"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                                        </div>
                                        <div className="fw-vert-name">Health-<br />care</div>
                                    </div>
                                    <div className="fw-vert v6">
                                        <div className="fw-vert-icon">
                                            <svg viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                                        </div>
                                        <div className="fw-vert-name">B2B /<br />SaaS</div>
                                    </div>
                                    <div className="fw-vert v7">
                                        <div className="fw-vert-icon">
                                            <svg viewBox="0 0 24 24"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/></svg>
                                        </div>
                                        <div className="fw-vert-name">Startups &amp;<br />Scale-ups</div>
                                    </div>
                                </div>

                                {/* Connector dots */}
                                <div className="fw-connectors">
                                    <div className="fw-conn-dot" /><div className="fw-conn-dot" /><div className="fw-conn-dot" /><div className="fw-conn-dot" /><div className="fw-conn-dot" /><div className="fw-conn-dot" /><div className="fw-conn-dot" />
                                </div>

                                {/* Horizontal foundation layers */}
                                <div className="fw-layers">
                                    {/* Common Assets */}
                                    <div className="fw-layer l-common">
                                        <div className="fw-layer-top">
                                            <div className="fw-layer-label">{t('ind.fwCommonAssets')}</div>
                                        </div>
                                        <div className="fw-layer-assets">
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/></svg>
                                                {t('ind.fwDataModels')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                                                {t('ind.fwReusableComponents')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                                                {t('ind.fwCodeLibraries')}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Foundation Architecture */}
                                    <div className="fw-layer l-found">
                                        <div className="fw-layer-top">
                                            <div className="fw-layer-label">{t('ind.fwFoundationArch')}</div>
                                        </div>
                                        <div className="fw-layer-assets">
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>
                                                {t('ind.fwAgenticArch')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                                {t('ind.fwAPIs')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
                                                {t('ind.fwCloudPlatforms')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                {t('ind.fwDatasets')}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Governance */}
                                    <div className="fw-layer l-gov">
                                        <div className="fw-layer-top">
                                            <div className="fw-layer-label">{t('ind.fwGovernance')}</div>
                                        </div>
                                        <div className="fw-layer-assets">
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                                                {t('ind.fwControls')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                                {t('ind.fwSecurity')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                                {t('ind.fwCompliance')}
                                            </span>
                                            <span className="fw-asset">
                                                <svg viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                                {t('ind.fwRiskManagement')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════ INDUSTRY TABS ═══════════════════════════ */}
            <IndustryTabs />

            {/* ═══════════════════════════ CTA STRIP ═══════════════════════════ */}
            <section className="ind-cta-strip" id="industries-cta">
                <div className="container">
                    <div className="section-label reveal" style={{ justifyContent: 'center' }}>{t('cta.nextStep')}</div>
                    <h2 className="ind-cta-headline reveal">
                        {renderT(t('ind.ctaHeadline'))}
                    </h2>
                    <p className="ind-cta-sub reveal reveal-d1">
                        {t('ind.ctaSub')}
                    </p>
                    <div className="ind-cta-btns reveal reveal-d2">
                        <Link to={lp('/contact#contact-form')} className="btn-primary">
                            {t('cta.startDiscovery')}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </Link>
                        <Link to={lp('/services')} className="btn-secondary">{t('cta.seeMethodology')}</Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Industries;
